import {
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  ReactNode
} from 'react'
import styled from 'styled-components'

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || ''

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string
          callback?: (token: string) => void
          'expired-callback'?: () => void
          'error-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
        }
      ) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
    onTurnstileLoad?: () => void
  }
}

export type TurnstileWidgetHandle = {
  reset: () => void
}

type TurnstileWidgetProps = {
  onToken: (token: string | null) => void
  className?: string
}

const Wrapper = styled.div<{
  children?: ReactNode
  className: string
  ref?: any
}>`
  display: flex;
  justify-content: center;
  margin: 16px 0;
  min-height: 65px;
`

let scriptLoadingPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve()
  }
  if (scriptLoadingPromise) {
    return scriptLoadingPromise
  }
  scriptLoadingPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      'script[src*="challenges.cloudflare.com/turnstile"]'
    )
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Turnstile'))
      )
      return
    }
    const script = document.createElement('script')
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Turnstile'))
    document.head.appendChild(script)
  })
  return scriptLoadingPromise
}

export const TurnstileWidget = forwardRef<
  TurnstileWidgetHandle,
  TurnstileWidgetProps
>(function TurnstileWidget({ onToken, className }, ref) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken

  const reset = useCallback(() => {
    onTokenRef.current(null)
    if (widgetIdRef.current != null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current)
    }
  }, [])

  useImperativeHandle(ref, () => ({ reset }), [reset])

  useEffect(() => {
    let cancelled = false

    const mount = async () => {
      if (!SITE_KEY || !containerRef.current) {
        return
      }
      try {
        await loadTurnstileScript()
        if (cancelled || !containerRef.current || !window.turnstile) {
          return
        }
        if (widgetIdRef.current != null) {
          window.turnstile.remove(widgetIdRef.current)
          widgetIdRef.current = null
        }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token: string) => {
            onTokenRef.current(token)
          },
          'expired-callback': () => {
            onTokenRef.current(null)
          },
          'error-callback': () => {
            onTokenRef.current(null)
          }
        })
      } catch {
        onTokenRef.current(null)
      }
    }

    mount()

    return () => {
      cancelled = true
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          // ignore cleanup errors
        }
        widgetIdRef.current = null
      }
    }
  }, [])

  if (!SITE_KEY) {
    return (
      <Wrapper className={className}>
        <span style={{ fontSize: 12, color: '#666' }}>
          Verificação de segurança indisponível
        </span>
      </Wrapper>
    )
  }

  return <Wrapper className={className} ref={containerRef} />
})
